import { Box, Button, Paper, Typography, FormControl } from "@mui/material";
import { CustomInput } from "../../../components/common/Input/CustomInput";
import { colorVariables, customValue } from "../../../utils/colorVariable";
import { useNavigate } from "react-router-dom";
import { ChangeEvent, FormEvent, useCallback, useState } from "react";
import { EROUTER } from "../../../utils/enumVariable";
import { register } from "../../../services/authService";
import { toast } from "react-toastify";
import { IRegister } from "../model/auth";
import { useTheme, useMediaQuery } from "@mui/material";
// eslint-disable-next-lin
import {
  validateEmail,
  validatePassword,
  validatePhone,
} from "../../../utils/validate";
import { RegisterLeftContent } from "../../../components/layouts/RegisterLeftContent";
import { LINKS } from "../../../constants/menu";
import dayjs from "dayjs";

const INITIAL_STATE: IRegister = {
  email: "",
  password: "",
  matricule: "",
  firstName: "",
  lastName: "",
  jobTitle: "",
  phone: "",
};

export const RegisterPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  // State
  const [registerState, setRegisterState] = useState<IRegister>(INITIAL_STATE);
  const [errors, setErrors] = useState<Partial<IRegister>>({});

  // Media Queries
  const breakpoints = {
    isMobile: useMediaQuery(theme.breakpoints.down("sm")),
    isLarge: useMediaQuery(theme.breakpoints.up("lg")),
    isExtraLarge: useMediaQuery(theme.breakpoints.up("xl")),
  };

  const cleanedPhone = registerState.phone.replace(/\s/g, "");
  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<IRegister> = {};

    if (!validateEmail(registerState.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!validatePassword(registerState.password)) {
      newErrors.password =
        "Password must be at least 8 characters, with uppercase, numbers, and special characters";
    }

    if (!validatePhone(cleanedPhone)) {
      newErrors.phone =
        "Phone must start with 034, 037, 032, 038, or 033 and be 10 digits long";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [cleanedPhone, registerState.email, registerState.password]);

  // Handlers
  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = event.target;
      setRegisterState((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!validateForm()) {
        toast.error("Please fix the errors in the form");
        return;
      }

      try {
        const response = await register(registerState);

        if (response) {
          toast.success("Registration successful!");
          navigate(EROUTER.SIGNIN);
          setErrors({});
        } else {
          throw new Error("Registration failed");
        }
      } catch (error) {
        toast.error((error as Error).message);
      }
    },
    [validateForm, registerState, navigate]
  );

  return (
    <Box
      component={Paper}
      height={{ sm: "80vh", xs: "100vh" }}
      width={breakpoints.isExtraLarge ? "50vw" : "inherit"}
      mx={breakpoints.isExtraLarge || breakpoints.isMobile ? "auto" : 20}
      my={{ sm: 10 }}
      borderRadius={customValue.borderRadius}
      elevation={5}
      display={"flex"}
      flexDirection={"row"}
    >
      {breakpoints.isLarge && <RegisterLeftContent />}
      <Box
        width={breakpoints.isLarge ? "50%" : "100%"}
        display={"flex"}
        alignItems={"flex-start"}
        justifyContent={"center"}
        flexDirection={"column"}
        px={10}
        height={"auto"}
        gap={errors ? 1 : 2}
        component={"form"}
        onSubmit={handleSubmit}
      >
        <Box
          display={{ sm: "none", xs: "flex" }}
          width={"100%"}
          alignItems={"center"}
          justifyContent={"center"}
        >
          <img
            src={`${process.env.PUBLIC_URL}/accord_knits.png`}
            alt="Logo"
            style={{ height: 100 }}
          />
        </Box>

        <Typography
          variant={Object.keys(errors).length > 0 ? "h5" : "h3"}
          color={colorVariables.primary}
          fontWeight={"lighter"}
        >
          Register
        </Typography>

        {/* Email Input */}
        <FormControl fullWidth error={!!errors.email}>
          <CustomInput
            label="Email"
            name="email"
            placeholder="Enter your email"
            value={registerState.email}
            onChange={handleInputChange}
            type="email"
            error={errors.email}
          />
        </FormControl>

        {/* Password Input */}
        <FormControl fullWidth error={!!errors.password}>
          <CustomInput
            label="Password"
            name="password"
            placeholder="Enter your password"
            type="password"
            value={registerState.password}
            onChange={handleInputChange}
            error={errors.password}
          />
        </FormControl>

        {/* Phone Input */}
        <FormControl fullWidth error={!!errors.phone}>
          <CustomInput
            label="Phone"
            name="phone"
            placeholder="Enter your phone number"
            value={registerState.phone}
            onChange={handleInputChange}
            type="tel"
            error={errors.phone}
          />
        </FormControl>

        {/* Other Fields */}
        <CustomInput
          label="Matricule"
          name="matricule"
          placeholder="Enter your matricule"
          value={registerState.matricule}
          onChange={handleInputChange}
          type="text"
        />
        <CustomInput
          label="First Name"
          name="firstName"
          placeholder="Enter your first name"
          value={registerState.firstName}
          onChange={handleInputChange}
          type="text"
        />
        <CustomInput
          label="Last Name"
          name="lastName"
          placeholder="Enter your last name"
          value={registerState.lastName}
          onChange={handleInputChange}
          type="text"
        />
        <CustomInput
          label="Job Title"
          name="jobTitle"
          placeholder="Enter your job title"
          value={registerState.jobTitle}
          onChange={handleInputChange}
          type="text"
        />

        <Button
          fullWidth
          sx={{
            background: colorVariables.gradient_primary,
            color: colorVariables.white,
          }}
          type="submit"
        >
          Register
        </Button>
        <Typography variant="caption">
          You have an account ?{" "}
          <span
            style={{
              fontWeight: "bolder",
              cursor: "pointer",
              color: colorVariables.primary,
            }}
            onClick={() => navigate(LINKS.SIGNIN)}
          >
            Sign in
          </span>
        </Typography>
        <Typography
          sx={{
            mx: "auto",
            fontSize: Object.keys(errors).length > 0 ? ".6em" : ".7em",
          }}
          variant="caption"
        >
          © {dayjs(new Date()).format("YYYY")} Accord Knits SA, Antananarivo,
          Madagascar
        </Typography>
      </Box>
    </Box>
  );
};
